//
// Copyright (c) 2011-2024 by Curalate, Inc.
//
import moment from 'moment-timezone';
import { XYCoord } from 'react-dnd';

import { getShopTags as getShopTagsApi } from '@apis/instagram-shops/instagramShopsApi';
import { IInstagramShopsProduct } from '@apis/instagram-shops/instagramShopsApiModel';
import { PostDetailsUserTagErrorMessage } from '@instagram-account/post-details/postDetailsUIConstants';
import { IShopTag } from '@models/IShopTag';
import { ISpatialTagCoordinates } from '@models/ISpatialTagCoordinates';
import { ITaggedUser } from '@models/ITaggedUser';
import { isDefined } from '@utils/filterPredicates';
import { getRequestErrorMessage } from '@utils/getRequestErrorMessage';
import { SINGLE_INSTAGRAM_USERNAME_REGEX } from '@utils/social-text-utils/instagramTextUtils';

export const getPositionsFromSpatialTagCoordinates = (
  coordinates: ISpatialTagCoordinates,
  width: number,
  height: number,
): XYCoord => ({
  x: coordinates.x * width,
  y: coordinates.y * height,
});

export const getSpatialTagCoordinatesFromPositions = (
  positions: XYCoord,
  width: number,
  height: number,
): ISpatialTagCoordinates => ({
  x: Math.max(0, Math.min(1, positions.x / width)),
  y: Math.max(0, Math.min(1, positions.y / height)),
});

const INSTAGRAM_USERNAME_MAX_LENGTH = 30;

/**
 * Validates a username to show error UI while a new user tag is in progress. Returns
 * nothing if the username is valid, and the error message to show if it is invalid.
 */
export const validateUserTag = (
  username: string,
  mediaIndex: number,
  currentTaggedUsers: ITaggedUser[],
): PostDetailsUserTagErrorMessage | undefined => {
  if (
    currentTaggedUsers.some(
      (userTag) =>
        userTag.username === username && userTag.mediaIndex === mediaIndex,
    )
  ) {
    return PostDetailsUserTagErrorMessage.DuplicateUsername;
  } else if (
    !SINGLE_INSTAGRAM_USERNAME_REGEX.test(username) ||
    username.length > INSTAGRAM_USERNAME_MAX_LENGTH
  ) {
    return PostDetailsUserTagErrorMessage.InvalidCharacters;
  }
};

interface InstagramTag {
  mediaIndex: number;
}

/**
 * Updates tagged users' media indices when media is reordered.
 */
export const reorderTags = <T extends InstagramTag>(
  currentTags: T[],
  currentCuidIndex: number,
  newCuidIndex: number,
): T[] => {
  return currentTags.map((tag) => {
    const currentTagIndex = tag.mediaIndex;
    let newTagIndex = currentTagIndex;

    if (currentTagIndex === currentCuidIndex) {
      // Tagged user is on the media being moved,
      // set tagged user index to media's new index
      newTagIndex = newCuidIndex;
    } else if (
      currentTagIndex >= newCuidIndex &&
      currentTagIndex < currentCuidIndex
    ) {
      // Media is being moved from after this tagged user
      // to before it, move this tagged user up one position
      newTagIndex += 1;
    } else if (
      currentTagIndex > currentCuidIndex &&
      currentTagIndex <= newCuidIndex
    ) {
      // Media is being moved from before this tagged user
      // to after it, move this tagged user down one position
      newTagIndex -= 1;
    }

    return {
      ...tag,
      mediaIndex: newTagIndex,
    };
  });
};

/**
 * Removes tagged products/users at given index and decrements
 * media indices of tagged products/users after index.
 */
export const removeTagsAtIndex = <T extends InstagramTag>(
  currentTags: T[],
  removeIndex: number,
): T[] => {
  if (removeIndex < 0) {
    return currentTags;
  } else {
    return currentTags
      .filter((tag) => tag.mediaIndex !== removeIndex)
      .map((tag) => {
        const currentTagIndex = tag.mediaIndex;

        if (currentTagIndex > removeIndex) {
          return {
            ...tag,
            mediaIndex: currentTagIndex - 1,
          };
        } else {
          return tag;
        }
      });
  }
};

/**
 * If the input time has elapsed, return "Now + 1 minute"  instead.
 * This is used to handle scheduling edge cases.
 */
export const getNowIfTimeHasElapsed = (time: Date) => {
  if (time < new Date()) {
    return moment().add(1, 'minute').toDate();
  }
  return time;
};

export const getShopTags = async (
  shopTags: IShopTag[],
): Promise<IShopTag[]> => {
  const catalogId = shopTags[0]?.catalogId;

  if (!shopTags.length || !isDefined(catalogId)) {
    return [];
  }

  try {
    const { successfulShopTags, failedShopTags } = await getShopTagsApi(
      catalogId,
      {
        shopTagIds: shopTags.map((s) => s.productId),
      },
    );

    logFailedShopTags(failedShopTags);

    return successfulShopTags
      .map((product) => {
        const shopTag = shopTags.find(
          (shopTag) => shopTag.productId === product.productId,
        );

        if (isDefined(shopTag)) {
          return {
            ...shopTag,
            ...product,
          };
        } else {
          return undefined;
        }
      })
      .filter(isDefined);
  } catch (e) {
    console.log(
      `[getShopTags] Error while getting shop tags: ${
        getRequestErrorMessage(e) ?? ''
      }`,
    );
    return [];
  }
};

export const logFailedShopTags = (failedShopTags: Record<string, string>) => {
  const entries = Object.entries(failedShopTags);

  if (entries.length) {
    console.log('Failed to get the following shop tags:');
    entries.map(([shopTagId, errorMessage]) => {
      console.log(`  ${shopTagId}: ${errorMessage}`);
    });
  }
};

export const flattenProductMatchVariants = (
  shopTags: IInstagramShopsProduct[],
): IInstagramShopsProduct[] => {
  return shopTags.flatMap((product) =>
    [product].concat(
      product.productVariants.map((productVariant) => ({
        ...product,
        productId: productVariant.productId,
        productName: `${product.productName} - ${productVariant.variantName}`,
      })),
    ),
  );
};
